import React, { FC, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router';

import TreeList, {
  Column,
  ColumnChooser,
  FilterRow,
  Lookup,
  Pager,
  Paging,
  Scrolling,
  Selection,
} from 'devextreme-react/tree-list';

import { AppCard } from 'components/appComponents';
import { useAuth } from 'context/auth';
import { Button, DropDownButton } from 'devextreme-react';
import $createDataSource from 'utils/create-data-souce-instance';
import $createStore from 'utils/create-store-instance';
import { useConfirmDialog } from 'utils/hooks';
import { isSuccess } from 'utils/responseStatus';

import { useGetMethodOfReceptionListQuery } from 'bus/methodOfReception/services';
import { getInvoice, getReportPassport, getTtnParameters } from 'bus/report/actions';
import {
  InvoiceParams,
  InvoiceParamsRequestPreInfo,
  ReportPassport,
  SetReportPassportRequest,
  TtnParameter,
  TtnParameterSetRequest,
} from 'bus/report/types';
import {
  useCancelSplitRequestWorkMutation,
  useGetRequestQuery,
  useGetRequestWorkStatisticQuery,
  useRequestWorkRedirectMutation,
} from 'bus/request';
import { RequestItem, RequestWorkForm } from 'bus/request/types';

import { useDispatchPromise } from 'hooks/useDispatchPromise';

import { DetailTaskInfo } from '..';
import {
  AdditionalCreateWorkRequestModal,
  AdjustmentCreateWorkRequestModal,
  CreateWorkRequestModal,
  DeleteWorkRequestModal,
  MethodOfReception,
  ReportPassportModal,
  SplitWorkRequestModal,
} from './components';
import { ReportInvoice, ReportModal, ReportTtn2Modal, TransferRequestModal } from './components';
import { alignments } from './utils';

const CREATE_ITEMS = [
  { id: 1, name: 'Отгрузка' },
  { id: 2, name: 'Корректировка' },
];

type Props = {
  isArch?: boolean;
};

const WorkList: FC<Props> = ({ isArch = false }) => {
  let { id } = useParams<{ id: string }>();
  const { getClaims }: any = useAuth();
  const dispatch = useDispatchPromise();
  const { getConfirmation } = useConfirmDialog();

  const workListData = useMemo(() => {
    const defaultSort = [{ selector: 'id', desc: true }];
    return $createDataSource(`/v1/Request/${id}/works`, 'id', { defaultSort });
  }, [id]);

  const { data: requestDetail, refetch: refetchRequestDetail } = useGetRequestQuery(
    { id },
    { skip: Boolean(!id) },
  );

  const { refetch: methodOfReceptionListRefetch } = useGetMethodOfReceptionListQuery({ requestId: id });

  const [cancelSplitRequestWork] = useCancelSplitRequestWorkMutation();

  const { data: statisticData, refetch } = useGetRequestWorkStatisticQuery({ id });
  const [requestWorkRedirect, { isLoading: isLoadingRedirect }] = useRequestWorkRedirectMutation();

  const requestStatuses: any = useMemo(() => $createStore('/v1/RequestWork/statuses', 'id'), []);

  let refGrid = useRef<TreeList | null>(null);

  const [visible, setVisible] = useState<boolean>(false);
  const [visibleAdjustment, setVisibleAdjustment] = useState<boolean>(false);
  const [visibleAdditional, setVisibleAdditional] = useState<boolean>(false);
  const [visibleDelete, setVisibleDelete] = useState<boolean>(false);
  const [openId, setOpenId] = useState<number | null>(null);
  const [openIdDelete, setOpenIdDelete] = useState<number | null>(null);
  const [dataReport, setDataReport] = useState<TtnParameterSetRequest | null>(null);
  const [dataInvoice, setDataInvoice] = useState<InvoiceParamsRequestPreInfo | null>(null);
  const [reportModalVisible, setReportModalVisible] = useState(false);
  const [report2ModalVisible, setReport2ModalVisible] = useState(false);
  const [invoiceModalVisible, setInvoiceModalVisible] = useState(false);
  const [transferModalVisible, setTransferModalVisible] = useState(false);
  const [passportModalVisible, setPassportModalVisible] = useState(false);
  const [splitModalVisible, setSplitModalVisible] = useState(false);
  const [dataPassport, setDataPassport] = useState<SetReportPassportRequest | null>(null);

  const [typeAdditionalModal, setTypeAdditionalModal] = useState<'CREATE' | 'EDIT'>('CREATE');
  const [selectTRequestWorkId, setSelectTRequestWorkId] = useState<number | null>(null);
  const [selectedWorksRows, setSelectedWorksRows] = useState<RequestItem[]>([]);
  const [selectedWorkRequestType, setSelectedWorkRequestType] = useState<0 | 1 | 2 | 3 | null>(null);

  const refresh = () => {
    refGrid.current?.instance.refresh();
    methodOfReceptionListRefetch();
    refetch();
  };

  const openDeleteModal = (requestWorkId: number) => {
    setOpenIdDelete(requestWorkId);
    setVisibleDelete(true);
  };
  const openEditModal = (requestWorkId: number) => {
    setOpenId(requestWorkId);
    setVisible(true);
  };

  const reportTtnParameters = async (requestWorkId: number) => {
    const params: TtnParameter = await dispatch(getTtnParameters.async({ requestWorkId }));
    setDataReport({ ...params, requestWorkId, version: 1 });
    setReportModalVisible(true);
  };

  const reportTtn2Parameters = async (requestWorkId: number) => {
    const params: TtnParameter = await dispatch(getTtnParameters.async({ requestWorkId }));
    const refreshed = id ? await refetchRequestDetail() : undefined;
    const latest = refreshed?.data ?? requestDetail;
    setDataReport({
      ...params,
      requestWorkId,
      version: 2,
      builderId: latest?.builderId ?? null,
      consigneeId: latest?.consigneeId ?? null,
    });
    setReport2ModalVisible(true);
  };

  const reportInvoiceParameters = async (requestWorkId: number) => {
    const dataReport: InvoiceParams = await dispatch(getInvoice.async({ requestWorkId }));
    setDataInvoice({ ...dataReport, requestWorkId });
    setInvoiceModalVisible(true);
  };

  const reportPassportParameters = async (requestWorkId: number) => {
    const dataReport: ReportPassport = await dispatch(getReportPassport.async({ requestWorkId }));
    setDataPassport({ ...dataReport, requestWorkId });
    setPassportModalVisible(true);
  };

  const submitRequestRedirect = async (data: RequestWorkForm) => {
    await requestWorkRedirect({
      methodOfReceptionId: data.methodOfReceptionId,
      requestId: data.requestId!,
      requestWorkIds: selectedWorksRows.length ? selectedWorksRows.map((i) => i.id!) : [selectTRequestWorkId!],
    }).then((data) => {
      if (isSuccess(data)) {
        setTransferModalVisible(false);
        refresh();
      }
    });
  };

  const openSplitModal = (requestWorkId: number) => {
    setOpenId(requestWorkId);
    setSplitModalVisible(true);
  };

  const cancelSplit = async (requestWorkId: number) => {
    const isConfirmed = await getConfirmation({
      title: 'Отмена переноса',
      message: 'Вы точно хотите отменить частичный перенос?',
      textConfirmButton: 'Да',
    });

    if (isConfirmed) {
      await cancelSplitRequestWork({ requestWorkId })
        .unwrap()
        .then(() => {
          refresh();
        });
    }
  };

  const reportModalClose = () => setReportModalVisible(false);
  const report2ModalClose = () => setReport2ModalVisible(false);
  const invoiceModalClose = () => setInvoiceModalVisible(false);
  const transferModalClose = () => setTransferModalVisible(false);
  const passportModalClose = () => setPassportModalVisible(false);
  const splitModalClose = () => setSplitModalVisible(false);

  const onItemClickHandler = async (val: any, requestWorkId: number, parentId: number | null, type: 0 | 1 | 2 | 3) => {
    setSelectedWorkRequestType(type);

    if (val.itemData.id === 1) {
      reportTtnParameters(requestWorkId);
    }
    if (val.itemData.id === 2) {
      if (type === 1) {
        setTypeAdditionalModal('EDIT');
        setOpenId(requestWorkId);

        setVisibleAdditional(true);
      }

      if (type === 2) {
        setOpenId(requestWorkId);
        setVisibleAdjustment(true);
      }

      if (type === 0 || type === 3) {
        openEditModal(requestWorkId);
      }
    }
    if (val.itemData.id === 3) {
      openDeleteModal(requestWorkId);
    }
    if (val.itemData.id === 4) {
      reportTtn2Parameters(requestWorkId);
    }
    if (val.itemData.id === 5) {
      reportInvoiceParameters(requestWorkId);
    }
    if (val.itemData.id === 6) {
      setSelectTRequestWorkId(requestWorkId);
      setTransferModalVisible(true);
    }
    if (val.itemData.id === 7) {
      reportPassportParameters(requestWorkId);
    }
    if (val.itemData.id === 8) {
      if (type === 3) {
        cancelSplit(requestWorkId);
      } else {
        openSplitModal(requestWorkId);
      }
    }
    if (val.itemData.id === 9) {
      setOpenId(requestWorkId);
      setVisibleAdditional(true);
      setTypeAdditionalModal('CREATE');
    }
  };

  const onItemCreateClickHandler = async (val: any) => {
    if (val.itemData.id === 1) {
      setOpenId(null);
      setVisible(true);
    }
    if (val.itemData.id === 2) {
      setOpenId(null);
      setVisibleAdjustment(true);
    }
  };

  const handleButtonCreateClick = async () => {
    setOpenId(null);
    setVisible(true);
  };

  const onSelectionChanged = ({ selectedRowsData }: any) => {
    setSelectedWorksRows(selectedRowsData);
  };

  const isCanLookPrice = getClaims(['CanLookPrice']);

  return (
    <div>
      {dataReport && reportModalVisible && (
        <ReportModal dataRepost={dataReport!} onClose={reportModalClose} visible={reportModalVisible} />
      )}
      {dataReport && report2ModalVisible && (
        <ReportTtn2Modal dataRepost={dataReport!} onClose={report2ModalClose} visible={report2ModalVisible} />
      )}
      {dataInvoice && invoiceModalVisible && (
        <ReportInvoice dataRepost={dataInvoice!} onClose={invoiceModalClose} visible={invoiceModalVisible} />
      )}
      {transferModalVisible && (
        <TransferRequestModal
          visible={transferModalVisible}
          onClose={transferModalClose}
          onSubmit={submitRequestRedirect}
          isLoading={isLoadingRedirect}
        />
      )}
      {passportModalVisible && (
        <ReportPassportModal visible={passportModalVisible} onClose={passportModalClose} dataPassport={dataPassport} />
      )}
      {openId && splitModalVisible && (
        <SplitWorkRequestModal
          visible={splitModalVisible}
          onClose={splitModalClose}
          requestWorkId={openId}
          refresh={refresh}
        />
      )}

      <CreateWorkRequestModal
        id={openId}
        visible={visible}
        changeVisible={setVisible}
        refresh={refresh}
        selectedWorkRequestType={selectedWorkRequestType}
      />
      <AdjustmentCreateWorkRequestModal
        id={openId}
        requestId={Number(id)}
        visible={visibleAdjustment}
        changeVisible={setVisibleAdjustment}
        refresh={refresh}
      />
      <AdditionalCreateWorkRequestModal
        requestId={Number(id)}
        requestWorkId={openId!}
        visible={visibleAdditional}
        changeVisible={setVisibleAdditional}
        refresh={refresh}
        type={typeAdditionalModal}
      />
      <DeleteWorkRequestModal
        visible={visibleDelete}
        changeVisible={(show) => setVisibleDelete(show)}
        id={openIdDelete}
        refresh={refresh}
      />

      <DetailTaskInfo />

      <div className="mb-2">
        <MethodOfReception requestId={id} />
      </div>

      <div className="d-flex mb-1 justify-end align-center">
        <Button className="send mt-1 mr-2" icon="refresh" onClick={refresh} />
        {/* {!isArch && (
          <Button
            width={120}
            text="Добавить"
            type="default"
            onClick={() => {
              setOpenId(null);
              setVisible(true);
            }}
            className="mt-1"
          />
        )} */}
        {!isArch && (
          <DropDownButton
            text="Создать"
            items={CREATE_ITEMS}
            onItemClick={onItemCreateClickHandler}
            dropDownOptions={{
              width: 150,
            }}
            displayExpr="name"
            keyExpr="id"
            className="mt-1"
            splitButton={true}
            useSelectMode={false}
            onButtonClick={handleButtonCreateClick}
          />
        )}
      </div>
      <div className="container-dev">
        <AppCard>
          <TreeList
            dataSource={workListData as any}
            showBorders={false}
            loadPanel={{
              enabled: true,
              text: 'Загрузка',
            }}
            ref={refGrid}
            remoteOperations="auto"
            onRowPrepared={(e) => {
              if (e.rowType === 'data' && e.data.type === 3) {
                //@ts-ignore
                e.rowElement.style.backgroundColor = '#FFFF00';
              }
              if (e.rowType === 'data' && e?.node?.hasChildren) {
                e.rowElement.style.backgroundColor = '#D3D3D3';
              }
            }}
            onSelectionChanged={onSelectionChanged}
          >
            <ColumnChooser enabled={true} />
            <Selection mode="multiple" />
            <FilterRow visible={true} />
            <Column dataField="id" caption="id" visible={false} />
            <Column dataField="status" caption="Статус" width={150}>
              <Lookup dataSource={requestStatuses} valueExpr="id" displayExpr="name" />
            </Column>
            <Column dataField="date" caption="Дата создания" dataType="datetime" width={140} />
            <Column dataField="startedDate" caption="Дата начала" dataType="datetime" width={140} />
            <Column dataField="completedDate" caption="Дата завершения" dataType="datetime" width={140} />
            <Column dataField="number" caption="Номер" />
            <Column dataField="parentNumber" caption="Род. номер" />
            <Column dataField="factoryName" caption="Завод" />
            <Column dataField="quantity" caption="Объем" width={90} />
            <Column dataField="fault" caption="Погрешность" width={100} />
            <Column dataField="transport" caption="Транспорт" width={200} />
            <Column dataField="driver" caption="Водитель" />
            <Column dataField="operator" caption="Оператор" />
            <Column dataField="methodOfReception" caption="Метод приёма" />
            <Column dataField="price" caption="Стоимость" visible={isCanLookPrice} />

            <Column
              dataField=""
              caption=""
              width={50}
              cellRender={(data) => (
                <>
                  <DropDownButton
                    icon="overflow"
                    items={alignments(data.data.type)}
                    onItemClick={(e) => onItemClickHandler(e, data.key, data.data.parentId, data.data.type)}
                    dropDownOptions={{
                      width: 300,
                    }}
                    className="dropdown-dots"
                    displayExpr="name"
                    keyExpr="id"
                  />
                </>
              )}
            />
            <Scrolling mode="standard" />
            <Paging defaultPageSize={15} enabled />
            <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} showInfo />
          </TreeList>
          <div className="pa-1 d-flex flex-end justify-end">
            <span className="pr-2">
              <b>Всего по заказу: {statisticData?.totalRequest}</b>
            </span>
            <span className="pr-2">
              <b>Объём выполненных работ: {statisticData?.totalWorks}</b>
            </span>
            <span className="pr-2">
              <b>Объём c учетом погрешности: {statisticData?.totalWorksWithFault}</b>
            </span>
            <span className="pr-2">
              <b>Осталось: {((statisticData?.totalRequest ?? 0) - (statisticData?.totalWorks ?? 0)).toFixed(2)}</b>
            </span>
          </div>
        </AppCard>
      </div>
    </div>
  );
};

export default WorkList;
